import { useEffect } from 'react';
import { Link } from 'wouter';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlatformBadge } from '@/components/platform-badge';
import { Spinner } from '@/components/ui/spinner';
import { Bookmark, BookmarkX, TrendingUp, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useWatchlist } from '@/hooks/use-watchlist';
import { trackEvent, FUNNEL_EVENTS } from '@/lib/analytics';

export default function WatchlistPage() {
  const { items, isLoading, removeFromWatchlist } = useWatchlist();
  const { toast } = useToast();

  const handleRemove = async (id: number, marketId: string, platform: 'polymarket' | 'kalshi', question: string) => {
    try {
      await removeFromWatchlist(id);
      trackEvent(FUNNEL_EVENTS.WATCHLIST_REMOVED, {
        marketId,
        platform,
        source: 'watchlist',
      });
      toast({ title: 'Removed from watchlist', description: question.slice(0, 60) });
    } catch {
      toast({ title: 'Failed to remove', variant: 'destructive' });
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Bookmark className="h-8 w-8 text-primary" />
            Watchlist
          </h1>
          <p className="text-muted-foreground mt-1">
            Markets you're tracking — click any to get an AI analysis
          </p>
        </div>
        <Link href="/markets">
          <Button variant="outline">
            Browse Markets
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : !items || items.length === 0 ? (
        <Card>
          <CardContent className="py-20 text-center">
            <Bookmark className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No markets saved yet</h3>
            <p className="text-muted-foreground mb-6">
              Save markets you want to track by clicking the bookmark icon on any market.
            </p>
            <Link href="/markets">
              <Button>
                <TrendingUp className="mr-2 h-4 w-4" />
                Browse Markets
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Card key={item.id} className="group hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <PlatformBadge platform={item.platform} />
                    <span className="text-xs text-muted-foreground">
                      Added {new Date(item.addedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="font-medium line-clamp-2">{item.marketQuestion}</p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Link href={`/markets/${item.marketId}?platform=${item.platform}`}>
                    <Button size="sm" variant="outline">
                      Analyze
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  </Link>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemove(item.id, item.marketId, item.platform, item.marketQuestion)}
                  >
                    <BookmarkX className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
